import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  confirm,
  FormCheckbox,
  FormInput,
  FormRoot,
  Label,
  Modal,
  RadioGroup,
  RadioGroupItem,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import {
  CalendarClock,
  CalendarDays,
  DoorClosed,
  DoorOpen,
  PlusIcon,
  Settings,
  Sparkles,
} from "lucide-react";
import { z } from "zod";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { ChecklistItemFormSchema } from "@/modules/checklist/domain/contracts";
import type { ChecklistTypeDTO } from "@/modules/checklist/domain/contracts/type";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { config } from "@/shared/lib/config";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { checklistTypesApi } from "../api/client";
import {
  useChecklistTypes,
  useCreateChecklistType,
  useDeleteChecklistType,
  useUpdateChecklistType,
} from "../api/queries";
import { ChecklistItemsField } from "./ChecklistItemsField";
import { ChecklistTypeForm } from "./ChecklistTypeForm";

type DepartmentItem = { id: string; name: string };
type ChecklistTypeItem = LookupItem;

const ChecklistFormSchema = z.object({
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ Checklist"),
  description: z.string().optional().nullable(),
  typeId: z.string().min(1, "ກະລຸນາເລືອກປະເພດ"),
  departmentId: z
    .union([z.string(), z.null()])
    .transform((val) => (val === "" ? null : val))
    .nullable()
    .optional(),
  status: z.enum(["active", "draft", "inactive"]),
  scheduleType: z.enum(["daily", "opening", "closing", "scheduled"]),
  scheduleFrequency: z
    .enum(["daily", "weekly", "monthly"])
    .optional()
    .nullable(),
  scheduleDayOfWeek: z.number().int().min(1).max(7).optional().nullable(),
  scheduleDayOfMonth: z.number().int().min(1).max(31).optional().nullable(),
  requireAllItems: z.boolean(),
  requirePhoto: z.boolean(),
  requireNotesOnFail: z.boolean(),
  items: z.array(ChecklistItemFormSchema),
});
export type ChecklistFormValues = z.infer<typeof ChecklistFormSchema>;

const statusOptions = [
  { value: "active", label: "ໃຊ້ງານ" },
  { value: "draft", label: "ປັບປຸງ" },
  { value: "inactive", label: "ເລີກໃຊ້" },
];

const scheduleTypeWithIcons = [
  { value: "daily", label: "ທຸກມື້", Icon: CalendarDays },
  { value: "opening", label: "ເປີດຮ້ານ", Icon: DoorOpen },
  { value: "closing", label: "ປິດຮ້ານ", Icon: DoorClosed },
  { value: "scheduled", label: "ຕາມກຳນົດ", Icon: CalendarClock },
] as const;

const scheduleFrequencyOptions = [
  { value: "daily", label: "ທຸກວັນ" },
  { value: "weekly", label: "ທຸກອາທິດ" },
  { value: "monthly", label: "ທຸກເດືອນ" },
];

const dayOfWeekOptions = [
  { value: "1", label: "ວັນຈັນ" },
  { value: "2", label: "ວັນອັງຄານ" },
  { value: "3", label: "ວັນພຸດ" },
  { value: "4", label: "ວັນພະຫັດ" },
  { value: "5", label: "ວັນສຸກ" },
  { value: "6", label: "ວັນເສົາ" },
  { value: "7", label: "ວັນອາທິດ" },
];

// Type Management Component
function TypeManagementButton() {
  const canManage = useActionPermission([
    "checklists:create",
    "checklists:update",
    "checklists:delete",
  ]);
  const listModal = useDisclosure();
  const createModal = useDisclosure();
  const editModal = useDisclosure<ChecklistTypeDTO>();
  const listQuery = useChecklistTypes({ limit: 100, offset: 0 });
  const createType = useCreateChecklistType();
  const updateType = useUpdateChecklistType();
  const deleteType = useDeleteChecklistType();

  if (!canManage) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={() => listModal.open()}
        title="ຈັດການປະເພດ"
      >
        <Settings className="h-4 w-4" />
      </Button>

      {/* Types List Modal */}
      <Modal
        open={!!listModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            listModal.close();
            createModal.close();
            editModal.close();
          }
        }}
        title="ຈັດການປະເພດ"
        description="ສ້າງ, ແກ້ໄຂ, ຫຼື ລຶບປະເພດ Checklist"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                listModal.close();
                createModal.open();
              }}
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              ສ້າງປະເພດໃໝ່
            </Button>
          </div>

          {listQuery.isLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              ກຳລັງໂຫຼດ...
            </div>
          ) : listQuery.data?.data.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              ຍັງບໍ່ມີປະເພດ
            </div>
          ) : (
            <div className="space-y-2">
              {listQuery.data?.data.map((type) => (
                <div
                  key={type.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{type.name}</span>
                    <span className="text-muted-foreground text-xs">
                      ລໍາດັບ: {type.orderIndex}
                    </span>
                    {!type.isActive && (
                      <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground text-xs">
                        ບໍ່ໃຊ້ງານ
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        listModal.close();
                        editModal.openWith(type);
                      }}
                    >
                      ແກ້ໄຂ
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        if (
                          await confirm({
                            title: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບປະເພດ "${type.name}"?`,
                            description:
                              "ປະເພດ Checklist ນີ້ຈະລຶບອອກຈາກປະເພດທັງຫມົດ",
                            actionText: "ລຶບ",
                            ActionProps: {
                              variant: "destructive",
                            },
                          })
                        ) {
                          await deleteType.mutateAsync(type.id);
                        }
                      }}
                    >
                      ລຶບ
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Create Type Modal */}
      <Modal
        open={!!createModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            createModal.close();
          } else {
            listModal.close();
          }
        }}
        title="ສ້າງປະເພດ"
        description="ກໍານົດຊື່, ລໍາດັບ, ແລະສະຖານະຂອງປະເພດ."
        size="sm"
      >
        <ChecklistTypeForm
          onSubmit={async (vals) => {
            await createType.mutateAsync({
              name: vals.name || "",
              orderIndex: vals.orderIndex ?? 0,
              isActive: vals.isActive ?? true,
            });
            createModal.close();
            listModal.open();
          }}
          submitting={createType.isPending}
        />
      </Modal>

      {/* Edit Type Modal */}
      <Modal
        open={!!editModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            editModal.close();
          } else {
            listModal.close();
          }
        }}
        title="ແກ້ໄຂປະເພດ"
        description="ປັບປຸງລາຍລະອຽດຂອງປະເພດ."
        size="sm"
      >
        <ChecklistTypeForm
          initialValues={
            editModal.data
              ? {
                  name: editModal.data.name,
                  orderIndex: editModal.data.orderIndex,
                  isActive: editModal.data.isActive,
                }
              : undefined
          }
          onSubmit={async (vals) => {
            if (!editModal.data) return;
            await updateType.mutateAsync({
              id: editModal.data.id,
              input: {
                name: vals.name || "",
                orderIndex: vals.orderIndex ?? 0,
                isActive: vals.isActive ?? true,
              },
            });
            editModal.close();
            listModal.open();
          }}
          submitting={updateType.isPending}
        />
      </Modal>
    </>
  );
}

export function ChecklistForm({
  initialValues,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<ChecklistFormValues>;
  onSubmit: (values: ChecklistFormValues) => void;
  submitting?: boolean;
}) {
  const methods = RHF.useForm<ChecklistFormValues>({
    resolver: zodResolver(ChecklistFormSchema) as never,
    defaultValues: {
      title: "",
      description: null,
      typeId: "",
      departmentId: null,
      status: "draft",
      scheduleType: "daily",
      scheduleFrequency: null,
      scheduleDayOfWeek: null,
      scheduleDayOfMonth: null,
      requireAllItems: false,
      requirePhoto: false,
      requireNotesOnFail: false,
      items: [],
      ...initialValues,
    },
  });

  // Check if in dev mode
  const isDevMode =
    import.meta.hot !== undefined || import.meta.env?.MODE === "development";

  // Generate random data for auto fill
  const generateRandomData = async () => {
    const titles = [
      "ກວດສອບການເປີດຮ້ານ",
      "ກວດສອບການປິດຮ້ານ",
      "ກວດສອບຄຸນນະພາບສິນຄ້າ",
      "ກວດສອບການບັນຍັດ",
      "ກວດສອບປະຈຳວັນ",
      "ກວດສອບການຈັດສົ່ງ",
      "ກວດສອບການຮັບສິນຄ້າ",
      "ກວດສອບສາງສິນຄ້າ",
    ];

    const descriptions = [
      "ລາຍການກວດສອບສຳລັບການເປີດຮ້ານ",
      "ລາຍການກວດສອບສຳລັບການປິດຮ້ານ",
      "ລາຍການກວດສອບຄຸນນະພາບກ່ອນຈັດສົ່ງ",
      "ລາຍການກວດສອບການບັນຍັດສິນຄ້າ",
      "ລາຍການກວດສອບປະຈຳວັນ",
    ];

    const itemTitles = [
      "ກວດສອບອຸປະກອນ",
      "ກວດສອບຄວາມສະອາດ",
      "ກວດສອບຄວາມປອດໄພ",
      "ກວດສອບຄຸນນະພາບ",
      "ກວດສອບຈຳນວນ",
      "ກວດສອບສະຖານະ",
      "ກວດສອບລະບົບ",
      "ກວດສອບສິ່ງເສດເຫຼືອ",
    ];

    const itemDescriptions = [
      "ກວດສອບວ່າອຸປະກອນທັງໝົດເຮັດວຽກປົກກະຕິ",
      "ກວດສອບຄວາມສະອາດຂອງພື້ນທີ່",
      "ກວດສອບຄວາມປອດໄພຂອງອຸປະກອນ",
      "ກວດສອບຄຸນນະພາບຂອງສິນຄ້າ",
      "ກວດສອບຈຳນວນສິນຄ້າ",
      "ກວດສອບສະຖານະການເຮັດວຽກ",
      "ກວດສອບລະບົບຕ່າງໆ",
    ];

    // Get first checklist type for typeId
    let typeId = "";
    try {
      const typesResult = await checklistTypesApi.list({ limit: 1, offset: 0 });
      const firstType = typesResult.data?.[0];
      if (firstType?.id) {
        typeId = firstType.id;
      }
    } catch {
      // If fetching types fails, leave typeId empty (user can select manually)
    }

    const randomTitle = titles[Math.floor(Math.random() * titles.length)] ?? "";
    const randomDescription =
      descriptions[Math.floor(Math.random() * descriptions.length)] ?? "";

    // Generate random items (3-6 items)
    const itemCount = Math.floor(Math.random() * 4) + 3;
    const randomItems = Array.from({ length: itemCount }, (_, i) => {
      const itemTitle =
        itemTitles[Math.floor(Math.random() * itemTitles.length)];
      const itemDescription =
        itemDescriptions[Math.floor(Math.random() * itemDescriptions.length)];

      return {
        id: undefined,
        title: `${itemTitle} ${i + 1}`,
        description: itemDescription,
        order: i,
        isRequired: Math.random() > 0.3, // 70% chance of being required
        allowNotes: Math.random() > 0.5, // 50% chance of allowing notes
      };
    });

    // Set form values
    if (typeId) {
      methods.setValue("typeId", typeId);
    }
    methods.setValue("title", randomTitle);
    methods.setValue("description", randomDescription);
    methods.setValue("status", Math.random() > 0.5 ? "active" : "draft");
    methods.setValue(
      "scheduleType",
      ["daily", "opening", "closing", "scheduled"][
        Math.floor(Math.random() * 4)
      ] as "daily" | "opening" | "closing" | "scheduled",
    );
    methods.setValue("requireAllItems", Math.random() > 0.5);
    methods.setValue("requirePhoto", Math.random() > 0.7); // 30% chance
    methods.setValue("requireNotesOnFail", Math.random() > 0.5);
    methods.setValue("items", randomItems);

    toast.success("Auto-filled form with random data");
  };

  return (
    <FormRoot<ChecklistFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-6"
    >
      <Card data-tourid="form-basic" className="gap-0">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">ຂໍ້ມູນພື້ນຖານ</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div data-tourid="form-title">
            <FormInput
              name="title"
              label="ຊື່ Checklist"
              requiredMark
              placeholder="ຕົວຢ່າງ: Checklist ກວດສອບການເປີດຮ້ານ"
            />
          </div>
          <div data-tourid="form-description">
            <FormInput
              name="description"
              label="ຄໍາອະທິບາຍ"
              placeholder="ອະທິບາຍເພີ່ມເຕີມ (ບໍ່ບັງຄັບ)"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2" data-tourid="form-type">
              <div className="flex items-center gap-2">
                <FormInfiniteCombobox<ChecklistTypeItem>
                  queryKey={["checklist-types", "form"]}
                  name="typeId"
                  label="ເລືອກປະເພດ"
                  requiredMark
                  queryFn={(args) =>
                    fetchLookupForInfinite(
                      `${config.apiUrl}/checklists/types/lookup`,
                      {
                        search: args.search,
                        pageParam: args.pageParam,
                      },
                    )
                  }
                  preloadQueryFn={(id) =>
                    hydrateLookupItem(
                      `${config.apiUrl}/checklists/types/lookup`,
                      id,
                    )
                  }
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  placeholder="ເລືອກປະເພດ Checklist"
                  className="flex-1"
                />
                <TypeManagementButton />
              </div>
            </div>
            <div data-tourid="form-department">
              <FormInfiniteCombobox<DepartmentItem>
                name="departmentId"
                label="ພະແນກ"
                queryKey={["checklists-departments"]}
                queryFn={(args) =>
                  fetchLookupForInfinite(
                    `${config.apiUrl}/checklists/departments/lookup`,
                    args,
                  )
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem(
                    `${config.apiUrl}/checklists/departments/lookup`,
                    id,
                  )
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                placeholder="ເລືອກພະແນກ (ບໍ່ບັງຄັບ)"
                clearable
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-tourid="form-schedule" className="gap-0">
        <CardHeader>
          <CardTitle className="text-base">ກຳນົດການໃຊ້ງານ</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3" data-tourid="form-schedule-type">
            <Label className="font-medium text-sm">ຊ່ວງເວລາໃຊ້ງານ</Label>
            <RHF.Controller
              control={methods.control}
              name="scheduleType"
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="grid grid-cols-2 gap-3 md:grid-cols-4"
                >
                  {scheduleTypeWithIcons.map(({ value, label, Icon }) => (
                    <Label
                      key={value}
                      htmlFor={`schedule-type-${value}`}
                      className={cn(
                        "flex cursor-pointer flex-col items-center gap-2 rounded-lg border p-3 text-center text-sm transition-colors hover:bg-muted/50",
                        field.value === value
                          ? "border-primary bg-primary/10 ring-1 ring-primary/20"
                          : "border-border",
                      )}
                    >
                      <RadioGroupItem
                        value={value}
                        id={`schedule-type-${value}`}
                        className="sr-only"
                      />
                      <Icon className="h-6 w-6 shrink-0 text-muted-foreground" />
                      <span className="font-medium">{label}</span>
                    </Label>
                  ))}
                </RadioGroup>
              )}
            />
          </div>

          {methods.watch("scheduleType") === "scheduled" && (
            <div
              className="space-y-4 rounded-lg border border-border/80 bg-muted/30 p-4"
              data-tourid="form-schedule-config"
            >
              <p className="font-medium text-muted-foreground text-sm">
                ກຳນົດວັນທີ່ຕ້ອງເຮັດ (ບໍ່ມີການຄຳນວນ/ແຈ້ງເຕືອນຢູ່ຕອນນີ້ — ເກັບເປັນຂໍ້ມູນເທົ່ານັ້ນ)
              </p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="font-medium text-sm">ຄວາມຖີ່</Label>
                  <RHF.Controller
                    control={methods.control}
                    name="scheduleFrequency"
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value ?? ""}
                        onValueChange={(v) =>
                          field.onChange(
                            v === ""
                              ? null
                              : (v as "daily" | "weekly" | "monthly"),
                          )
                        }
                        className="flex flex-wrap gap-3"
                      >
                        {scheduleFrequencyOptions.map((opt) => (
                          <Label
                            key={opt.value}
                            htmlFor={`schedule-frequency-${opt.value}`}
                            className={cn(
                              "flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2.5 text-sm transition-colors hover:bg-muted/50",
                              field.value === opt.value
                                ? "border-primary bg-primary/10 ring-1 ring-primary/20"
                                : "border-border",
                            )}
                          >
                            <RadioGroupItem
                              value={opt.value}
                              id={`schedule-frequency-${opt.value}`}
                              className="sr-only"
                            />
                            {opt.label}
                          </Label>
                        ))}
                      </RadioGroup>
                    )}
                  />
                </div>
                {methods.watch("scheduleFrequency") === "weekly" && (
                  <div className="space-y-2">
                    <Label className="font-medium text-sm">ວັນໃນອາທິດ</Label>
                    <RHF.Controller
                      control={methods.control}
                      name="scheduleDayOfWeek"
                      render={({ field }) => (
                        <RadioGroup
                          value={field.value != null ? String(field.value) : ""}
                          onValueChange={(v) =>
                            field.onChange(v === "" ? null : Number(v))
                          }
                          className="flex flex-wrap gap-2"
                        >
                          {dayOfWeekOptions.map((opt) => (
                            <Label
                              key={opt.value}
                              htmlFor={`schedule-day-week-${opt.value}`}
                              className="cursor-pointer"
                            >
                              <RadioGroupItem
                                value={opt.value}
                                id={`schedule-day-week-${opt.value}`}
                                className="sr-only"
                              />
                              <Badge
                                variant={
                                  field.value === Number(opt.value)
                                    ? "default"
                                    : "outline"
                                }
                                className={cn(
                                  "transition-colors hover:opacity-90",
                                  field.value === Number(opt.value) &&
                                    "ring-1 ring-primary/30",
                                )}
                              >
                                {opt.label}
                              </Badge>
                            </Label>
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </div>
                )}
                {methods.watch("scheduleFrequency") === "monthly" && (
                  <div className="space-y-2">
                    <Label className="font-medium text-sm">
                      ວັນທີໃນເດືອນ (1–31)
                    </Label>
                    <RHF.Controller
                      control={methods.control}
                      name="scheduleDayOfMonth"
                      render={({ field }) => (
                        <RadioGroup
                          value={field.value != null ? String(field.value) : ""}
                          onValueChange={(v) =>
                            field.onChange(v === "" ? null : Number(v))
                          }
                          className="flex flex-wrap gap-2"
                        >
                          {Array.from({ length: 31 }, (_, i) => {
                            const val = String(i + 1);
                            const selected =
                              field.value != null && field.value === i + 1;
                            return (
                              <Label
                                key={val}
                                htmlFor={`schedule-day-month-${val}`}
                                className="cursor-pointer"
                              >
                                <RadioGroupItem
                                  value={val}
                                  id={`schedule-day-month-${val}`}
                                  className="sr-only"
                                />
                                <Badge
                                  variant={selected ? "default" : "outline"}
                                  className={cn(
                                    "min-w-8 transition-colors hover:opacity-90",
                                    selected && "ring-1 ring-primary/30",
                                  )}
                                >
                                  {val}
                                </Badge>
                              </Label>
                            );
                          })}
                        </RadioGroup>
                      )}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-3" data-tourid="form-status">
            <Label className="font-medium text-sm">ສະຖານະ</Label>
            <RHF.Controller
              control={methods.control}
              name="status"
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="flex flex-wrap gap-3"
                >
                  {statusOptions.map((opt) => (
                    <Label
                      key={opt.value}
                      htmlFor={`status-${opt.value}`}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2.5 text-sm transition-colors hover:bg-muted/50",
                        field.value === opt.value
                          ? "border-primary bg-primary/10 ring-1 ring-primary/20"
                          : "border-border",
                      )}
                    >
                      <RadioGroupItem
                        value={opt.value}
                        id={`status-${opt.value}`}
                        className="sr-only"
                      />
                      {opt.label}
                    </Label>
                  ))}
                </RadioGroup>
              )}
            />
          </div>
        </CardContent>
      </Card>

      <Card data-tourid="form-items-card" className="gap-0">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">ລາຍການກວດສອບ</CardTitle>
        </CardHeader>
        <CardContent>
          <div data-tourid="form-items">
            <ChecklistItemsField
              control={methods.control}
              watch={methods.watch}
              setValue={methods.setValue}
            />
          </div>
        </CardContent>
      </Card>

      <Card data-tourid="form-settings" className="gap-0">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">ຕັ້ງຄ່າ</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <FormCheckbox name="requireAllItems" label="ຕ້ອງຕິກໃຫ້ຄົບທຸກຂໍ້ກ່ອນຈົບ" />
            <FormCheckbox name="requirePhoto" label="ຕ້ອງຖ່າຍຮູບແນບ" />
            <FormCheckbox name="requireNotesOnFail" label="ຕ້ອງໃສ່ໝາຍເຫດຖ້າບໍ່ຕິກ" />
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between border-t pt-4">
        {isDevMode && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={generateRandomData}
            className="text-xs"
          >
            <Sparkles className="mr-1 h-3 w-3" />
            Auto Fill (Dev)
          </Button>
        )}
        <div className="ml-auto flex gap-2" data-tourid="form-submit">
          <Button type="submit" isLoading={submitting}>
            ບັນທຶກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
